import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Trophy, Award, Target, Flame, Heart, Skull, LogIn } from "lucide-react";
import { useLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import NavBar from "@/components/NavBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GamesProfile() {
  const { lang } = useLang();
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const T = lang === "fr" ? FR : EN;

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/games/me/stats`, { withCredentials: true })
      .then(r => setData(r.data));
  }, [user]);

  if (!user) return (
    <div className="min-h-screen bg-white">
      <NavBar />
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="games-profile-login">
        <h1 className="font-serif-mystery text-3xl mb-4">👤 {T.title}</h1>
        <p className="text-zinc-600 mb-6">{T.loginNeed}</p>
        <Button onClick={login} className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-11 px-5">
          <LogIn size={16} className="mr-2" /> {T.login}
        </Button>
      </div>
    </div>
  );

  const s = data?.stats || {};
  const earned = data?.achievements || [];

  return (
    <div className="min-h-screen bg-white" data-testid="games-profile-page">
      <NavBar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/games" className="text-sm text-zinc-500 hover:text-[#8b6f3d] flex items-center gap-1"><ArrowLeft size={14} /> {T.back}</Link>
        <h1 className="font-serif-mystery text-4xl mt-3 mb-6">👤 {T.title}</h1>

        <div className="flex items-center gap-4 mb-8">
          {user.picture ? <img src={user.picture} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[#8b6f3d]" /> :
            <div className="w-20 h-20 rounded-full bg-zinc-200 flex items-center justify-center text-3xl">{user.username?.[0]?.toUpperCase()}</div>}
          <div>
            <div className="font-serif-mystery text-2xl">{user.username || user.name}</div>
            {user.team_name && <div className="text-sm text-[#8b6f3d]">🏆 {user.team_name}</div>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Kpi icon={<Trophy />} label={T.bestScore} value={(s.best_score || 0).toLocaleString()} testId="stat-best-score" />
          <Kpi icon={<Skull />} label={T.kills} value={s.total_kills || 0} testId="stat-kills" />
          <Kpi icon={<Flame />} label={T.bestCombo} value={`x${s.best_combo || 0}`} testId="stat-best-combo" />
          <Kpi icon={<Target />} label={T.bestWave} value={s.best_wave || 0} testId="stat-best-wave" />
        </div>

        <h2 className="font-serif-mystery text-2xl mb-3">🏅 {T.achievements}</h2>
        <p className="text-sm text-zinc-500 mb-4">{earned.length} / {data?.achievements_total || 0}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="achievements-grid">
          {(data?.achievements || []).map(a => (
            <div key={a.key} className="border border-emerald-300 bg-emerald-50/50 rounded-xl p-4 text-center" data-testid={`ach-${a.key}`}>
              <div className="text-4xl mb-2">{a.icon}</div>
              <div className="font-medium text-sm">{a[lang]}</div>
            </div>
          ))}
          {earned.length === 0 && <p className="col-span-full text-center text-zinc-500 py-4">{T.noAch}</p>}
        </div>

        <h2 className="font-serif-mystery text-2xl mt-8 mb-3">📍 {T.missions}</h2>
        <p className="text-zinc-600">{data?.missions_count || 0} {T.missionsCompleted}</p>
      </div>
    </div>
  );
}
function Kpi({ icon, label, value, testId }) {
  return (
    <div className="border border-zinc-200 rounded-xl p-4 text-center" data-testid={testId}>
      <div className="w-10 h-10 mx-auto rounded-full bg-[#8b6f3d]/10 text-[#8b6f3d] flex items-center justify-center mb-2">{icon}</div>
      <div className="font-serif-mystery text-2xl">{value}</div>
      <div className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}
const FR = { back: "Retour", title: "Mon profil joueur", loginNeed: "Connecte-toi pour voir tes statistiques de jeu.", login: "Se connecter avec Google", bestScore: "Meilleur score", kills: "Éliminations", bestCombo: "Meilleur combo", bestWave: "Meilleure vague", achievements: "Succès", noAch: "Aucun succès débloqué pour l'instant. Élimine des créatures pour en gagner !", missions: "Missions terrain", missionsCompleted: "missions complétées" };
const EN = { back: "Back", title: "My gamer profile", loginNeed: "Sign in to see your game stats.", login: "Sign in with Google", bestScore: "Best score", kills: "Kills", bestCombo: "Best combo", bestWave: "Best wave", achievements: "Achievements", noAch: "No achievements yet. Eliminate creatures to earn them!", missions: "Field missions", missionsCompleted: "missions completed" };
